"use client";

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import { api } from "./api";

interface User {
  id: number;
  name: string;
  email: string;
  avatar_url: string | null;
  onboarding_completed: boolean;
  account_type: string;
  is_superadmin: boolean;
  org_role: string | null;
  org_name: string | null;
  org_logo: string | null;
  orgs?: Array<{
    id: number;
    name: string;
    role: string;
    logo_url: string | null;
    account_type?: string;
  }>;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, recaptchaToken?: string) => Promise<void>;
  register: (name: string, email: string, password: string, recaptchaToken?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isImpersonating: boolean;
  impersonatorName: string | null;
  impersonatorEmail: string | null;
  impersonate: (userId: number) => Promise<void>;
  stopImpersonating: () => Promise<void>;
}

// ── API Response type ────────────────────────────────────────────

interface MeResponse {
  id: number;
  email: string;
  name: string;
  avatar_url: string | null;
  onboarding_completed?: boolean;
  is_superadmin?: boolean;
  account_type?: string;
  orgs?: Array<{
    id: number;
    name: string;
    role: string;
    logo_url?: string | null;
    account_type?: string;
  }>;
}

function mapMeToUser(res: MeResponse): User {
  const orgs = (Array.isArray(res.orgs) ? res.orgs : []).map((o: {
    id: number;
    name: string;
    role: string;
    logo_url?: string | null;
    account_type?: string;
  }) => ({
    id: o.id,
    name: o.name,
    role: o.role,
    logo_url: o.logo_url ?? null,
    account_type: o.account_type ?? "individual",
  }));

  // Keep API primary account_type (often personal). Company memberships live in orgs[].
  // Display fields prefer company when present so the shell reflects team context.
  const companyOrg = orgs.find((o: { account_type?: string }) => o.account_type === "company");
  const personalOrg = orgs.find((o: { account_type?: string }) => (o.account_type || "individual") === "individual");
  const displayOrg = companyOrg ?? personalOrg ?? orgs[0] ?? null;

  return {
    id: res.id,
    email: res.email,
    name: res.name,
    avatar_url: res.avatar_url,
    onboarding_completed: res.onboarding_completed ?? true,
    account_type: res.account_type ?? personalOrg?.account_type ?? displayOrg?.account_type ?? "individual",
    is_superadmin: res.is_superadmin ?? false,
    org_role: displayOrg?.role ?? null,
    org_name: displayOrg?.name ?? null,
    org_logo: displayOrg?.logo_url ?? null,
    orgs,
  };
}

const AuthContext = createContext<AuthState | null>(null);

// Helper to clear all stored auth state and redirect to login
function clearAuthAndRedirect(targetPath?: string) {
  localStorage.removeItem("mc_token");
  localStorage.removeItem("mc_refresh");
  localStorage.removeItem("mc_admin_token");
  localStorage.removeItem("mc_admin_refresh");
  localStorage.removeItem("mc_admin_name");
  localStorage.removeItem("mc_admin_email");
  localStorage.removeItem("mc_impersonating");
  localStorage.removeItem("mc_active_org_id");
  document.cookie = "mc_session=; path=/; max-age=0";
  const redirectParam = targetPath && targetPath !== "/" && !targetPath.startsWith("/login")
    ? `?redirect=${encodeURIComponent(targetPath)}`
    : "";
  // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- plain function outside component, no router available
  window.location.href = `/login${redirectParam}`;
}

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [impersonatorName, setImpersonatorName] = useState<string | null>(null);
  const [impersonatorEmail, setImpersonatorEmail] = useState<string | null>(null);
  const initialized = useRef(false);
  const healthIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Skip re-init on HMR — only run once
    if (initialized.current) return;
    initialized.current = true;

    if (typeof window !== "undefined") {
      const imp = localStorage.getItem("mc_impersonating") === "true";
      // eslint-disable-next-line react-hooks/set-state-in-effect -- legitimate SSR-conditional localStorage read
      setIsImpersonating(imp);
      setImpersonatorName(localStorage.getItem("mc_admin_name"));
      setImpersonatorEmail(localStorage.getItem("mc_admin_email"));
    }

    const token = localStorage.getItem("mc_token");
    if (token) {
      api
        .me()
        .then((res) => {
          // MeService returns null when user does not exist (deleted)
          if (!res) {
            clearAuthAndRedirect(window.location.pathname);
            return;
          }
          setUser(mapMeToUser(res));
        })
        .catch((err) => {
          // Clear tokens and redirect on auth errors (expired/invalid/revoked token)
          if (err.message === "Unauthorized" || err.message === "Invalid token" || err.message === "Token has expired") {
            clearAuthAndRedirect(window.location.pathname);
            return;
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // LocalStorage token check — runs every 30 seconds while user is logged in.
  // Detects manual removal of mc_token via DevTools.
  // Pure JS, no network calls — cheap to run frequently.
  const storagePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!user) {
      if (storagePollRef.current) {
        clearInterval(storagePollRef.current);
        storagePollRef.current = null;
      }
      return;
    }

    const check = () => {
      if (!localStorage.getItem("mc_token")) {
        clearAuthAndRedirect(window.location.pathname);
      }
    };

    // Run immediately, then every 30 seconds
    check();
    storagePollRef.current = setInterval(check, 30 * 1000);

    return () => {
      if (storagePollRef.current) {
        clearInterval(storagePollRef.current);
        storagePollRef.current = null;
      }
    };
  }, [user]);

  // Server-side session health check — runs every 5 minutes while user is logged in.
  // Detects session revocation, user deletion, or token expiry via the API.
  useEffect(() => {
    if (!user) {
      if (healthIntervalRef.current) {
        clearInterval(healthIntervalRef.current);
        healthIntervalRef.current = null;
      }
      return;
    }

    const check = () => {
      api
        .me()
        .then((res) => {
          if (!res) {
            clearAuthAndRedirect(window.location.pathname);
          }
        })
        .catch((err) => {
          if (!localStorage.getItem("mc_token")) {
            clearAuthAndRedirect(window.location.pathname);
            return;
          }
          if (err.message === "Unauthorized" || err.message === "Invalid token" || err.message === "Token has expired") {
            clearAuthAndRedirect(window.location.pathname);
          }
        });
    };

    healthIntervalRef.current = setInterval(check, 5 * 60 * 1000);

    return () => {
      if (healthIntervalRef.current) {
        clearInterval(healthIntervalRef.current);
        healthIntervalRef.current = null;
      }
    };
  }, [user]);

  // Listen for storage events from other tabs/windows.
  // When mc_token is removed in another tab, this tab will immediately redirect.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === "mc_token" && !e.newValue) {
        clearAuthAndRedirect(window.location.pathname);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const login = useCallback(async (email: string, password: string, recaptchaToken?: string) => {
    const res = await api.login(email, password, recaptchaToken);
    localStorage.setItem("mc_token", res.access_token);
    localStorage.setItem("mc_refresh", res.refresh_token);
    // Set session cookie so the server-side middleware knows we're logged in
    document.cookie = "mc_session=1; path=/; SameSite=Lax; max-age=604800";
    // Fetch actual user state (onboarding status, account type, etc.)
    const me = await api.me();
    setUser(mapMeToUser(me));
  }, []);

  const register = useCallback(async (name: string, email: string, password: string, recaptchaToken?: string) => {
    await api.register(name, email, password, recaptchaToken);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(mapMeToUser(me));
    } catch { /* ignore */ }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    clearAuthAndRedirect();
  }, []);

  const impersonate = useCallback(async (userId: number) => {
    const adminToken = localStorage.getItem("mc_token");
    const adminRefresh = localStorage.getItem("mc_refresh");
    if (!adminToken) throw new Error("No admin session found");

    // Fetch current admin profile to store admin name/email
    const adminMe = await api.me();
    const adminName = adminMe.name || "Admin";
    const adminEmail = adminMe.email || "";

    // Call backend impersonation endpoint
    const res = await api.impersonateUser(userId);

    // Save original admin details
    localStorage.setItem("mc_admin_token", adminToken);
    localStorage.setItem("mc_admin_refresh", adminRefresh || "");
    localStorage.setItem("mc_admin_name", adminName);
    localStorage.setItem("mc_admin_email", adminEmail);
    localStorage.setItem("mc_impersonating", "true");

    // Set the impersonated user tokens
    localStorage.setItem("mc_token", res.access_token);
    localStorage.setItem("mc_refresh", res.refresh_token);

    // Update states
    setIsImpersonating(true);
    setImpersonatorName(adminName);
    setImpersonatorEmail(adminEmail);

    // Refresh user profile (will now fetch target user's details because token is theirs)
    await refreshUser();
  }, [refreshUser]);

  const stopImpersonating = useCallback(async () => {
    const adminToken = localStorage.getItem("mc_admin_token");
    const adminRefresh = localStorage.getItem("mc_admin_refresh");

    if (!adminToken) {
      logout();
      return;
    }

    try {
      await api.request("/v1/admin/stop-impersonation", { method: "POST" }).catch(() => {});
    } catch { /* ignore */ }

    // Restore admin session
    localStorage.setItem("mc_token", adminToken);
    if (adminRefresh) {
      localStorage.setItem("mc_refresh", adminRefresh);
    } else {
      localStorage.removeItem("mc_refresh");
    }

    // Clean up impersonation variables
    localStorage.removeItem("mc_admin_token");
    localStorage.removeItem("mc_admin_refresh");
    localStorage.removeItem("mc_admin_name");
    localStorage.removeItem("mc_admin_email");
    localStorage.removeItem("mc_impersonating");

    setIsImpersonating(false);
    setImpersonatorName(null);
    setImpersonatorEmail(null);

    // Refresh profile
    await refreshUser();
  }, [logout, refreshUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        refreshUser,
        isImpersonating,
        impersonatorName,
        impersonatorEmail,
        impersonate,
        stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
